import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import { Label } from "reactstrap";
import MultiSelect from "../../../../../../../components/MultiSelect";
import UserUtils from "src/services/utils/UserUtils";
import ReportIncidenService from "../../../../components/api/ReportIncidenService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CommonService from "src/modules/data-protection/apis/CommonService";

const AffectedLegals = (props) => {
    const { t, report, caseId, currentUser, reportLoading, Organization } = props;

    const [affectedLegals, setAffectedLegals] = useState([]);

    const userUtils = new UserUtils();
    const isAnalystOrAnalystAdmin = () => {
        return userUtils.isAnalystOrAnalystAdmin(currentUser);
    };

    const handleRemoveLegalMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ReportIncidenService.getInstance();

            return await service.removeLegal(payload);
        },
    });

    const handleAddLegalMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ReportIncidenService.getInstance();

            return await service.addLegal(payload);
        },
    });

    const handleFetchLegalEntities = useQuery({
        queryKey: ["gdpr-fetch-legal-entities-list"],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await service.legalEntities(Organization?.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching legal entities."), {
                type: "error",
            });
        },
    });

    const handleFetchReportLegals = useQuery({
        queryKey: ["gdpr-fetch-report-legal-entities-list", report?.case_id],
        queryFn: async () => {
            const service = ReportIncidenService.getInstance();
            return await service.fetchLegals({
                report_case_id: report?.case_id
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!report?.case_id,
        onError: () => {
            toast(t("An error occurred while fetching legal entities."), {
                type: "error"
            });
        },
    });

    const handleRemoveLegalChange = (item) => {
        handleRemoveLegalMutation.mutate(
            {
                reportCaseId: caseId,
                legalEntityId: item[0].id,
            },
            {
                onSuccess: () => {
                    toast(t("The list of affected legal entities has been updated successfully."), {
                        type: "success",
                    });
                },
                onError: () => {
                    toast(t("Failed to update affected legal entities."), {
                        type: "error",
                    });
                    setAffectedLegals([...affectedLegals]);
                },
            }
        );
    };

    const handleAddLegalChange = (item) => {
        handleAddLegalMutation.mutate(
            {
                reportCaseId: caseId,
                legalEntityId: item[0].id,
            },
            {
                onSuccess: () => {
                    toast(t("The list of affected legal entities has been updated successfully."), {
                        type: "success",
                    });
                },
                onError: () => {
                    toast(t("Failed to update affected legal entities."), {
                        type: "error",
                    });
                    setAffectedLegals([...affectedLegals]);
                },
            }
        );
    };

    const legalEntitiesLoading =
        handleFetchLegalEntities.isFetching || handleFetchReportLegals.isFetching;

    const determineChangesAndSendToBackend = (
        currentValues,
        previousValues,
        removeCallback,
        addCallback
    ) => {
        const removedItems = previousValues.filter(
            (item) => !currentValues.find((val) => val.id === item.id)
        );
        const addedItems = currentValues.filter(
            (val) => !previousValues.find((item) => item.id === val.id)
        );

        if (removedItems.length > 0 && addedItems.length === 0) {
            removeCallback(removedItems);
        } else if (addedItems.length > 0 && removedItems.length === 0) {
            addCallback(addedItems);
        } else {
            return;
        }
    };

    const handleMultiSelectChangeAssets = (selectedValues) => {
        determineChangesAndSendToBackend(
            selectedValues,
            affectedLegals,
            (removedItems) => {
                handleRemoveLegalChange(removedItems);
            },
            (addedItems) => {

                handleAddLegalChange(addedItems);
            }
        );
        setAffectedLegals(selectedValues);
    };

    useEffect(() => {
        if (handleFetchReportLegals.data?.legal_entities?.length > 0) {
            setAffectedLegals(handleFetchReportLegals.data.legal_entities);
        }
    }, [handleFetchReportLegals.data]);

    return (
        <div>
            <Label>{t("Affected Legal Entities")}</Label>
            {!legalEntitiesLoading && !reportLoading ? (
                <MultiSelect
                    options={handleFetchLegalEntities.data || []}
                    selectedValues={affectedLegals}
                    onChange={handleMultiSelectChangeAssets}
                    placeholder={t("Select") + "..."}
                    isDisabled={report?.deleted_by_name || report?.is_deleted_content || report?.is_read_only_analyst}
                />
            ) : (
                <>
                    <p
                        className={`dt-field dt-skeleton ${isAnalystOrAnalystAdmin ? "dt-select-list" : ""
                            }`}
                    ></p>
                    <p className="dt-field dt-skeleton dt-update-date"></p>
                </>
            )}
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token, currentUser } = state.Login;
    return {
        currentUser,
        token,
        Organization: state.Organization
    };
};

export default withNamespaces()(connect(mapStatetoProps)(AffectedLegals));
